import { FC } from "react";
import { format, parseISO, type Locale } from "date-fns";
import { ConnectProto } from "@racoon/api";
import { cn, IconButton } from "@racoon/ui";
import { IcCalendar, IcChevronDown, IcPencil, IcRemove } from "@racoon/icons";
import { Namespace, TranslationKey, useDateFnsLocale, useT } from "@racoon/i18n";
import { getEnumName, toDateLike } from "@racoon/lib";
import { usePreviewScheduleQuery } from "~/shared/api/container-group-schedule/container-group-schedule.grpc";
import { isLegacySchedule, isSimplified, scheduleToForm } from "../lib/schedule-form";
import { containerGroupName } from "../lib/schedule-label";
import { RulesEditor } from "./rules-editor";

type Props = {
  schedule: ConnectProto.ContainerGroupSchedule;
  containerGroup?: ConnectProto.ContainerGroup;
  expanded: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
};

/** "6 Янв 2014", as the design writes dates. */
const shortDate = (date: Date, locale: Locale) =>
  format(date, "d LLL yyyy", { locale })
    .replace(".", "")
    .replace(/\s(\S)/, (_, first: string) => ` ${first.toUpperCase()}`);

const DateChip: FC<{ date?: string; locale: Locale }> = ({ date, locale }) => (
  <span className="flex h-8 items-center gap-1 rounded-md border border-border-primary bg-white px-3 text-xs font-medium text-text-body">
    <IcCalendar className="size-5 text-brand-blue" />
    {date ? shortDate(parseISO(date), locale) : "—"}
  </span>
);

/** A schedule of the location (Figma 15647:26474): its header, contract, type and rules. */
export const ScheduleCard: FC<Props> = ({
  schedule,
  containerGroup,
  expanded,
  onToggle,
  onEdit,
  onDelete,
}) => {
  const t = useT();
  const locale = useDateFnsLocale();
  const legacy = isLegacySchedule(schedule);
  const form = scheduleToForm(schedule);
  // The view shows what the editor can: say so when the stored rules hold more.
  const simplified = !legacy && isSimplified(form);
  const jobType = getEnumName(ConnectProto.JobTypeSchema, schedule.jobType);
  const { currentData: preview } = usePreviewScheduleQuery(
    { source: { case: "scheduleId", value: schedule.id } },
    { skip: !expanded || legacy || form.kind !== "everyNDays" },
  );

  const contract = containerGroup?.contract;
  const contractStart = toDateLike(contract?.startAt);
  const contractEnd = toDateLike(contract?.endAt);
  // No provider of its own: the zone's first serves it.
  const depot =
    schedule.serviceProvider?.name ??
    (schedule.serviceProviderId
      ? t(Namespace.PickupLocation, "schedules.deletedProvider", {
          id: String(schedule.serviceProviderId),
        })
      : containerGroup?.serviceProviders?.[0]?.name);
  const label = "text-sm font-medium text-text-body";
  const secondary = "text-sm font-medium text-text-secondary";

  return (
    <div className="flex w-full flex-col gap-4 rounded-lg border border-border-primary bg-white p-3">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-4">
          <IconButton
            size="md"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-label={t(Namespace.Common, expanded ? "collapse" : "expand")}
          >
            <IcChevronDown
              className={cn("size-6 text-text-body transition-transform", expanded && "rotate-180")}
            />
          </IconButton>
          <span className="text-sm font-medium text-text-information">
            {containerGroupName(containerGroup, t)}
          </span>
          {containerGroup?.ownerOrganization?.name && (
            <span className={secondary}>{containerGroup.ownerOrganization.name}</span>
          )}
          {jobType && (
            <span className={secondary}>{t(Namespace.Enum, jobType as TranslationKey)}</span>
          )}
          {depot && <span className={secondary}>{depot}</span>}
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2.5">
            <span className={label}>{t(Namespace.PickupLocation, "schedules.card.validFrom")}</span>
            <DateChip date={schedule.validFrom} locale={locale} />
            <span className={label}>{t(Namespace.PickupLocation, "schedules.editor.validTo")}</span>
            <DateChip date={schedule.validTo} locale={locale} />
          </div>
          <div className="flex items-center gap-1">
            <IconButton size="md" onClick={onEdit} aria-label={t(Namespace.Common, "edit")}>
              <IcPencil className="size-5 text-brand-blue" />
            </IconButton>
            <IconButton size="md" onClick={onDelete} aria-label={t(Namespace.Common, "delete")}>
              <IcRemove className="size-5 text-text-error" />
            </IconButton>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-start justify-between gap-4">
        {contract ? (
          <span className="rounded-md bg-surface-muted p-2 text-sm font-medium text-text-body">
            {[
              contract.name || `№${contract.dealNumber}`,
              contractStart &&
                t(Namespace.PickupLocation, "schedules.card.from", {
                  date: shortDate(contractStart, locale),
                }),
              contractEnd &&
                t(Namespace.PickupLocation, "schedules.editor.contractUntil", {
                  date: shortDate(contractEnd, locale),
                }),
            ]
              .filter(Boolean)
              .join(" ")}
          </span>
        ) : (
          <span />
        )}
        <div className="flex items-center gap-4">
          <span className={label}>{t(Namespace.PickupLocation, "schedules.editor.kind")}</span>
          <span className="p-2 text-sm text-text-body">
            {legacy
              ? t(Namespace.PickupLocation, "schedules.legacy")
              : t(Namespace.PickupLocation, `schedules.editor.kinds.${form.kind}`)}
          </span>
        </div>
      </div>

      {expanded &&
        (legacy ? (
          <div className="flex flex-col gap-1 text-sm">
            <code className="text-text-body">{schedule.legacyExpr}</code>
            <span className="text-text-secondary">
              {t(Namespace.PickupLocation, "schedules.legacyHint")}
            </span>
          </div>
        ) : (
          <>
            {simplified && (
              <p className="text-xs text-text-secondary">
                {t(Namespace.PickupLocation, "schedules.card.simplified")}
              </p>
            )}
            <RulesEditor form={form} onChange={() => undefined} preview={preview} readOnly />
          </>
        ))}
    </div>
  );
};
